/** @jsxImportSource @emotion/react */
import Toast from 'components/atoms/Toast'
import {
  isNotMailedValid,
  NotMailedReasonFields,
} from 'components/lab/billingPatientStatements/NotMailedReasonFields'
import { apiErrorMessage } from 'components/lab/billingPatientStatements/statementFormat'
import { confirm } from 'components/molecules/Confirmation'
import React from 'react'
import { Modal } from 'react-bootstrap'
import {
  useMarkStatementNotMailedMutation,
  useMarkStatementReturnedMutation,
} from 'store/services/paperStatements'
import { StatementNotMailedDto, StatementNotMailedReason } from 'store/types'

const DEFAULT_NOT_MAILED: StatementNotMailedDto = { reason: StatementNotMailedReason.NEEDS_REPRINT, note: '' }

type NotMailedModalProps = {
  // The run item to change; the modal is shown while it is set.
  itemId: string | null
  onHide: () => void
}

// Undoes a confirmation for a statement that did not actually go out.
export const NotMailedModal: React.FC<NotMailedModalProps> = ({ itemId, onHide }) => {
  const [value, setValue] = React.useState<StatementNotMailedDto>(DEFAULT_NOT_MAILED)
  const [markNotMailed, { isLoading }] = useMarkStatementNotMailedMutation()

  React.useEffect(() => {
    setValue(DEFAULT_NOT_MAILED)
  }, [itemId])

  const handleSubmit = async () => {
    if (!itemId) return

    try {
      await markNotMailed({ itemId, reason: value.reason, note: value.note?.trim() || null }).unwrap()
      Toast({ type: 'success', label: 'Statement marked as not mailed' })
      onHide()
    } catch (error) {
      Toast({ type: 'error', label: apiErrorMessage(error, 'Unable to mark the statement as not mailed') })
    }
  }

  return (
    <Modal show={!!itemId} onHide={onHide} centered>
      <Modal.Header>
        <Modal.Title>Mark as not mailed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="textGray">Why didn&apos;t this statement go out?</p>
        <NotMailedReasonFields value={value} onChange={setValue} />
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-outline-secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={isLoading || !isNotMailedValid(value)}>
          {isLoading ? 'Saving…' : 'Mark as not mailed'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

// Came back from the post office: holds paper for the patient until the
// address is corrected.
export const useMarkStatementReturned = () => {
  const [markReturned] = useMarkStatementReturnedMutation()

  return async (itemId: string) => {
    try {
      await confirm('', {
        title: 'Mark as returned?',
        description: 'The statement came back from the post office. Paper statements for this patient '
          + 'will be held until their mailing address is corrected.',
        yesAction: 'Mark as returned',
        noAction: 'Cancel',
      })
    } catch {
      return
    }

    try {
      await markReturned(itemId).unwrap()
      Toast({ type: 'success', label: 'Statement marked as returned' })
    } catch (error) {
      Toast({ type: 'error', label: apiErrorMessage(error, 'Unable to mark the statement as returned') })
    }
  }
}
